// hooks/api/placement.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as placementService from "@/services/placement.service";
import {
  PlacementCreateType,
  PlacementParamsType,
  PlacementRecordType,
} from "@/services/placement.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const placementsKey = {
  all: ["placements"] as const,
  lists: (filters: PlacementParamsType) =>
    [...placementsKey.all, "lists", filters] as const,
  detail: (id: string) => [...placementsKey.all, "detail", id] as const,
};

export const usePlacementsQuery = (filters: PlacementParamsType) =>
  useQuery({
    queryKey: placementsKey.lists(filters),
    queryFn: () => placementService.fetchAllPlacements(filters),
    placeholderData: (prev) => prev,
  });

export const usePlacementByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: placementsKey.detail(id),
    queryFn: () => placementService.fetchPlacementById(id),
    enabled: enabled && !!id,
  });

export const useCreatePlacementMutation = (filters: PlacementParamsType) => {
  const queryClient = useQueryClient();
  return useMutation<PlacementRecordType, AxiosError, PlacementCreateType>({
    mutationFn: (data) => placementService.createPlacement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: placementsKey.lists(filters) });
      notifySuccess("Placement record added successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to add placement"),
  });
};

export const useUpdatePlacementMutation = (filters: PlacementParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ _id, data }: { _id: string; data: PlacementCreateType }) =>
      placementService.updatePlacement(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: placementsKey.lists(filters) });
      queryClient.invalidateQueries({
        queryKey: placementsKey.detail(variables._id),
      });
      notifySuccess("Placement record updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update placement"
      ),
  });
};

export const useDeletePlacementMutation = (filters: PlacementParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) => placementService.deletePlacement(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({ queryKey: placementsKey.lists(filters) });
      queryClient.removeQueries({ queryKey: placementsKey.detail(_id) });
      notifySuccess("Placement record deleted successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to delete placement"
      ),
  });
};
